'use client'

import { Icon } from '@/components/ui/icon'
import { getUserMenuItems } from '@/config/nav/user-menu-nav'
import { useCurrentUser } from '@/hooks/use-current-user'
import { useProfileCheck } from '@/hooks/use-profile-check'
import { getUserProfileAction } from '@/lib/services/actions/my/my-bio'
import {
  ButtonProps,
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@heroui/react'
import { Button } from '@/components/ui/button'
import { MediaPlacement } from '@prisma/client'
import { signIn, signOut } from 'next-auth/react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'

export default function UserMenu(props: ButtonProps) {
  const user = useCurrentUser()
  const pathname = usePathname()
  const refreshAttempts = useRef(0)
  const lastRefreshTime = useRef(0)
  const [refreshKey, setRefreshKey] = useState(0)
  const [isMounted, setIsMounted] = useState(false)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [isLoadingAvatar, setIsLoadingAvatar] = useState(false)
  const [avatarError, setAvatarError] = useState(false)
  const profileStatus = useProfileCheck()

  useEffect(() => {
    setIsMounted(true)
    return () => {
      refreshAttempts.current = 0
    }
  }, [])

  useEffect(() => {
    async function fetchUserAvatar() {
      if (!user?.id) return

      refreshAttempts.current = 0
      lastRefreshTime.current = 0
      setAvatarError(false)

      setIsLoadingAvatar(true)
      try {
        const isGoogleAccount = user.email?.endsWith('@gmail.com') || user.email?.includes('googlemail.com')
        const hasGoogleImage =
          user.image && (user.image.includes('googleusercontent.com') || user.image.includes('lh3.google'))

        if (isGoogleAccount && hasGoogleImage && refreshAttempts.current === 0) {
          try {
            const { syncUserAvatarAction } = await import('@/lib/services/actions/my/my-profile')
            await syncUserAvatarAction(user.id)
            await new Promise((resolve) => setTimeout(resolve, 500))
          } catch (syncError) {
            console.error('UserMenu: Failed to sync Google avatar:', syncError)
          }
        }

        const profileResponse = await getUserProfileAction()

        if (profileResponse.success && profileResponse.data) {
          const avatarMedia = profileResponse.data.media?.find(
            (media) => media.placement === MediaPlacement.AVATAR && media.url
          )

          if (avatarMedia?.url && typeof avatarMedia.url === 'string') {
            setAvatarUrl(avatarMedia.url)
            setRefreshKey(Date.now())
          } else if (user.image && typeof user.image === 'string') {
            setAvatarUrl(user.image)
            setRefreshKey(Date.now())
          } else {
            setAvatarUrl(null)
            setAvatarError(true)
          }
        } else if (user.image && typeof user.image === 'string') {
          setAvatarUrl(user.image)
          setRefreshKey(Date.now())
        } else {
          setAvatarUrl(null)
          setAvatarError(true)
        }
      } catch (error) {
        console.error('UserMenu: Error fetching user avatar:', error)
        if (user.image && typeof user.image === 'string') {
          setAvatarUrl(user.image)
          setRefreshKey(Date.now())
        } else {
          setAvatarUrl(null)
          setAvatarError(true)
        }
      } finally {
        setIsLoadingAvatar(false)
      }
    }

    if (user?.id && isMounted && !profileStatus.isChecking) {
      fetchUserAvatar()
    } else if (!user && isMounted) {
      setAvatarUrl(null)
      setAvatarError(false)
      refreshAttempts.current = 0
      lastRefreshTime.current = 0
    }
  }, [user, isMounted, profileStatus.isChecking])

  const forceRefreshAvatar = useCallback(async () => {
    if (!user?.id) return

    const now = Date.now()
    if (now - lastRefreshTime.current < 5000) return

    lastRefreshTime.current = now
    refreshAttempts.current += 1

    if (refreshAttempts.current > 3) return

    try {
      setIsLoadingAvatar(true)
      setAvatarError(false)

      const profileResponse = await getUserProfileAction()

      if (profileResponse.success && profileResponse.data) {
        const avatarMedia = profileResponse.data.media?.find((media) => media.placement === MediaPlacement.AVATAR)

        if (avatarMedia?.url) {
          setAvatarUrl(avatarMedia.url)
          refreshAttempts.current = 0
        } else if (user.image) {
          setAvatarUrl(user.image)
        } else {
          setAvatarError(true)
        }

        setRefreshKey((prev) => prev + 1)
      } else {
        console.error('Failed to refresh avatar:', profileResponse.error)
        setAvatarError(true)
      }
    } catch (error) {
      console.error('Error refreshing avatar:', error)
      setAvatarError(true)
    } finally {
      setIsLoadingAvatar(false)
    }
  }, [user?.id, user?.image])

  const displayImage = avatarUrl || user?.image
  const isLoading = isLoadingAvatar || profileStatus.isChecking
  const menuItems = user ? getUserMenuItems(user.role) : []

  return (
    <div className='relative flex items-center'>
      {isMounted && user && (
        <div className='items-center hidden mr-2 md:flex'>
          <span className='text-sm font-medium truncate max-w-[120px]'>{user.name || 'User'}</span>
        </div>
      )}

      {!isMounted || !user ? (
        <Button icon='sign-in' label='Sign in' onPress={() => signIn(undefined, { callbackUrl: pathname })} {...props}>
          <Icon icon='sign-in' className='w-5 h-5' />
        </Button>
      ) : (
        <Popover
          placement='bottom-end'
          offset={8}
        >
          <PopoverTrigger>
            <Button
              radius='full'
              variant='ghost'
              isIconOnly
              {...props}
              aria-label='User menu'
            >
              {!isLoading && displayImage && typeof displayImage === 'string' && !avatarError ? (
                <div className='relative w-full h-full overflow-hidden rounded-full'>
                  <Image
                    key={`avatar-${refreshKey}`}
                    src={displayImage}
                    alt={user.name || ''}
                    width={36}
                    height={36}
                    className='object-cover w-full h-full'
                    priority
                    unoptimized={false}
                    onError={() => {
                      console.error('Avatar image failed to load:', displayImage)
                      setAvatarError(true)

                      const isRelativePath = typeof displayImage === 'string' && displayImage.startsWith('/media/')
                      const isGoogleImage =
                        typeof displayImage === 'string' &&
                        (displayImage.includes('googleusercontent.com') || displayImage.includes('lh3.google'))
                      const isCdnUrl =
                        typeof displayImage === 'string' &&
                        displayImage.includes(process.env.NEXT_PUBLIC_MEDIA_CDN || 'media.choice.graphics')

                      if (refreshAttempts.current === 0 && (isRelativePath || isGoogleImage || isCdnUrl) && user?.id) {
                        refreshAttempts.current += 1
                        lastRefreshTime.current = Date.now()

                        try {
                          import('@/lib/services/actions/my/my-profile').then((module) => {
                            if (user && user.id) {
                              module
                                .syncUserAvatarAction(user.id)
                                .then((result) => {
                                  if (result.success) {
                                    setTimeout(() => {
                                      refreshAttempts.current = 0
                                      forceRefreshAvatar()
                                    }, 2000)
                                  } else {
                                    setAvatarUrl(null)
                                  }
                                })
                                .catch(() => {
                                  setAvatarUrl(null)
                                })
                            }
                          })
                        } catch {
                          setAvatarUrl(null)
                        }
                      } else if (refreshAttempts.current >= 3) {
                        setAvatarUrl(null)
                      }
                    }}
                  />
                </div>
              ) : (
                <Icon icon='user-rounded' className='w-5 h-5' />
              )}
            </Button>
          </PopoverTrigger>

          <PopoverContent
            className='p-0 w-[260px] rounded-xl overflow-hidden bg-surface border border-border shadow-[0_8px_30px_rgba(0,0,0,0.4)]'
          >
            {/* User info header */}
            <div className='px-4 py-4 border-b border-border'>
              <div className='flex items-center gap-3'>
                {displayImage && typeof displayImage === 'string' && !avatarError ? (
                  <div className='relative w-10 h-10 overflow-hidden rounded-full ring-2 shrink-0' style={{ '--tw-ring-color': 'var(--cw-avatar-ring, rgba(28, 49, 68, 0.20))' } as React.CSSProperties}>
                    <Image
                      src={displayImage}
                      alt={user.name || ''}
                      width={40}
                      height={40}
                      className='object-cover w-full h-full'
                      priority
                      onError={() => setAvatarError(true)}
                    />
                  </div>
                ) : (
                  <div
                    className='flex items-center justify-center w-10 h-10 rounded-full ring-2 shrink-0'
                    style={{ backgroundColor: 'var(--cw-avatar-bg, rgba(28, 49, 68, 0.08))', '--tw-ring-color': 'var(--cw-avatar-ring, rgba(28, 49, 68, 0.20))' } as React.CSSProperties}
                  >
                    <Icon icon='user-rounded' className='w-5 h-5' style={{ color: 'var(--cw-avatar-fg, var(--cw-ink))' }} />
                  </div>
                )}
                <div className='min-w-0'>
                  <p className='text-xs text-muted'>Signed in as</p>
                  <p className='font-semibold text-sm truncate text-foreground'>
                    {user.name || 'Anonymous'}
                  </p>
                </div>
              </div>
            </div>

            {/* Menu items */}
            <div className='py-1'>
              {menuItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}

                  className='flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-white/5 text-foreground'
                >
                  <Icon icon={item.icon} className='w-4 h-4 text-muted' />
                  {item.label}
                </Link>
              ))}
            </div>

            {/* Logout */}
            <div className='border-t border-border'>
              <button
                type='button'
                onClick={() => signOut({ callbackUrl: '/' })}
                className='flex items-center gap-3 w-full px-4 py-2.5 text-sm transition-colors hover:bg-white/5 text-danger'
              >
                <Icon icon='sign-out' className='w-4 h-4' />
                Log out
              </button>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}
